/**
 * BulkInspectionWizard - 3-step wizard for fleet-wide vehicle inspection
 *
 * Step 1: Vehicle selection + date/inspector
 * Step 2: Inspection grid (matrix of vehicles x resource items)
 * Step 3: Review + scoring + submit
 */
import React, { useState, useCallback } from 'react';
import { X, ClipboardCheck, ChevronLeft, ChevronRight, Save, Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import api from '../../../api.js';
import { apiRequest } from '../../../api/core.js';
import { useAuth } from '../../../v3/context/AuthContext.jsx';
import {
  RANGE_VALUES,
  RANGE_GLYPHS,
  RANGE_LABELS,
  ALL_GROUPS,
  GROUP_LABELS,
  RESOURCE_COLUMNS,
  VEHICLE_MAX_SCORE,
  DRIVER_RESOURCES,
  columnApplies,
  getScorableColumns,
  computeVehicleScore,
  groupStatus,
} from '../../../config/inspection.js';

import InspectionStepSelection from './inspection/InspectionStepSelection.jsx';
import InspectionStepGrid from './inspection/InspectionStepGrid.jsx';
import InspectionStepReview from './inspection/InspectionStepReview.jsx';

const STEP_LABELS = ['Selection', 'Inspection', 'Bilan'];

export default function BulkInspectionWizard({ isOpen, onClose, onSuccess, onMessage }) {
  const { user } = useAuth();

  // ---- data ----
  const { data: vehiclesRaw = [] } = useQuery({
    queryKey: ['vehicles'],
    queryFn: async () => {
      const res = await api.vehicle.getAll();
      return res.data || res || [];
    },
  });
  const vehicles = Array.isArray(vehiclesRaw) ? vehiclesRaw.filter(v => v.status !== 'retired') : [];

  // ---- wizard state ----
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedVehicleIds, setSelectedVehicleIds] = useState([]);
  const [inspectionDate, setInspectionDate] = useState(new Date().toISOString().split('T')[0]);
  const [inspectorName, setInspectorName] = useState('');
  const [readings, setReadings] = useState({}); // { [vehicleId]: { [resourceKey]: 0|1|2|3|null } }
  const [odometers, setOdometers] = useState({}); // { [vehicleId]: number }
  const [vehicleNotes, setVehicleNotes] = useState({}); // { [vehicleId]: string }
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Build vehicles map for quick lookup
  const vehiclesMap = {};
  for (const v of vehicles) {
    vehiclesMap[v.id] = v;
  }

  const selectedVehicles = selectedVehicleIds.map(id => vehiclesMap[id]).filter(Boolean);

  // Pre-fill odometers from vehicle.mileage when vehicles are selected
  const prevSelectedRef = React.useRef([]);
  React.useEffect(() => {
    const newIds = selectedVehicleIds.filter(id => !prevSelectedRef.current.includes(id));
    if (newIds.length > 0) {
      setOdometers(prev => {
        const updated = { ...prev };
        for (const id of newIds) {
          if (!updated[id] && vehiclesMap[id]?.mileage) {
            updated[id] = vehiclesMap[id].mileage;
          }
        }
        return updated;
      });
    }
    prevSelectedRef.current = selectedVehicleIds;
  }, [selectedVehicleIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- reading helpers ----
  const setReading = useCallback((vehicleId, key, value) => {
    setReadings(prev => ({
      ...prev,
      [vehicleId]: { ...(prev[vehicleId] || {}), [key]: value },
    }));
  }, []);

  const setOdometer = useCallback((vehicleId, km) => {
    setOdometers(prev => ({ ...prev, [vehicleId]: km }));
  }, []);

  const setNote = useCallback((vehicleId, note) => {
    setVehicleNotes(prev => ({ ...prev, [vehicleId]: note }));
  }, []);

  // ---- navigation ----
  const [incompleteWarningShown, setIncompleteWarningShown] = useState(false);
  const [overrideTimestamp, setOverrideTimestamp] = useState(0);

  const canProceedStep2 = () => {
    // Check if all groups have at least some data
    const now = Date.now();
    if (now - overrideTimestamp < 3000) return true; // override active

    for (const gid of ALL_GROUPS) {
      const gs = groupStatus(gid, selectedVehicleIds, readings);
      if (gs.status === 'empty') {
        if (!incompleteWarningShown) {
          setIncompleteWarningShown(true);
          return false;
        }
        // Second click within 3s
        setOverrideTimestamp(now);
        return true;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (currentStep === 1) {
      if (selectedVehicleIds.length === 0) {
        onMessage?.({ title: 'Sélection requise', message: 'Sélectionnez au moins un véhicule', type: 'warning' });
        return;
      }
      setCurrentStep(2);
    } else if (currentStep === 2) {
      if (!canProceedStep2()) return;
      setIncompleteWarningShown(false);
      setCurrentStep(3);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setIncompleteWarningShown(false);
      setCurrentStep(currentStep - 1);
    }
  };

  // ---- submit ----
  const handleSubmit = async () => {
    // DIAGNOSTIC: dump full state to console for debugging silent skips
    console.log('[BulkInspection] === SUBMIT DIAGNOSTIC ===');
    console.log('[BulkInspection] selectedVehicleIds:', JSON.stringify(selectedVehicleIds));
    console.log('[BulkInspection] readings keys:', Object.keys(readings));
    console.log('[BulkInspection] readings key types:', Object.keys(readings).map(k => `${k}(${typeof k})`));
    for (const vid of selectedVehicleIds) {
      const vr = readings[vid];
      const vrStr = readings[String(vid)];
      const filledDirect = vr ? Object.values(vr).filter(v => v !== null && v !== undefined).length : 0;
      const filledStr = vrStr ? Object.values(vrStr).filter(v => v !== null && v !== undefined).length : 0;
      console.log(`[BulkInspection] Vehicle ${vid} (${typeof vid}): direct=${filledDirect}, asString=${filledStr}, plate=${vehiclesMap[vid]?.license_plate || '?'}`);
    }

    // ========================================================================
    // STEP 0: Build a normalized readings map keyed by Number(vehicleId).
    // The `readings` state object may have keys stored as strings or numbers
    // depending on how they were set (grid click vs "Tout vert" shortcut).
    // We normalize ONCE here so the loop below never misses a vehicle.
    // ========================================================================
    const normalizedReadings = {};
    for (const [rawKey, value] of Object.entries(readings)) {
      normalizedReadings[Number(rawKey)] = value;
    }

    const normalizedOdometers = {};
    for (const [rawKey, value] of Object.entries(odometers)) {
      normalizedOdometers[Number(rawKey)] = value;
    }

    const normalizedNotes = {};
    for (const [rawKey, value] of Object.entries(vehicleNotes)) {
      normalizedNotes[Number(rawKey)] = value;
    }

    // ========================================================================
    // STEP 1: Pre-submit validation — BLOCK if any vehicle has no readings.
    // This prevents silent data loss. The user MUST go back and fix it.
    // ========================================================================
    const missingVehicles = selectedVehicleIds.filter(id => {
      const vr = normalizedReadings[Number(id)] || {};
      const filledCount = Object.values(vr).filter(v => v !== null && v !== undefined).length;
      return filledCount === 0;
    });

    if (missingVehicles.length > 0) {
      const plates = missingVehicles.map(id => vehiclesMap[id]?.license_plate || `#${id}`).join(', ');
      onMessage?.({
        title: 'Donnees manquantes',
        message: `${missingVehicles.length} vehicule(s) n'ont aucune mesure enregistree : ${plates}. Retournez a l'etape 2 pour completer l'inspection.`,
        type: 'error',
      });
      return;
    }

    // Validate odometers: prevent decrease
    const decreasedVehicles = selectedVehicleIds.filter(id => {
      const v = vehiclesMap[id];
      const entered = normalizedOdometers[Number(id)];
      return entered != null && v?.mileage && entered < v.mileage;
    });
    if (decreasedVehicles.length > 0) {
      const plates = decreasedVehicles.map(id => vehiclesMap[id]?.license_plate).join(', ');
      onMessage?.({
        title: 'Kilometrage invalide',
        message: `Le kilometrage ne peut pas diminuer pour : ${plates}`,
        type: 'error',
      });
      return;
    }

    // ========================================================================
    // STEP 2: Submit — all vehicles guaranteed to have data at this point.
    // ========================================================================
    // SINGLE API CALL: Build payload for all vehicles, send once to middleware.
    // The middleware fans out to the data layer internally — no gateway rate limit.
    // ========================================================================
    setIsSubmitting(true);
    try {
      const vehicleEntries = [];

      for (const vehicleId of selectedVehicleIds) {
        const vid = Number(vehicleId);
        const vr = normalizedReadings[vid] || {};
        const vehicle = vehiclesMap[vid] || vehiclesMap[vehicleId] || {};
        const odometerKm = normalizedOdometers[vid] || null;

        const vehicleReadings = Object.entries(vr)
          .filter(([_, val]) => val !== null && val !== undefined)
          .map(([key, idx]) => ({
            resource_type: key,
            value_percent: RANGE_VALUES[idx],
            notes: normalizedNotes[vid] || null,
          }));

        const scoreResult = computeVehicleScore(vid, normalizedReadings, vehicle);
        const score = typeof scoreResult === 'number' ? scoreResult : scoreResult.score;
        const criticalCount = vehicleReadings.filter(r => r.value_percent <= 30).length;

        const scorableCols = RESOURCE_COLUMNS.filter(c => columnApplies(c, vehicle) && !DRIVER_RESOURCES.has(c.key));
        const items = scorableCols
          .filter(col => vr[col.key] !== null && vr[col.key] !== undefined)
          .map(col => ({
            code: col.key,
            label: col.label,
            status: vr[col.key] >= 2 ? 'pass' : 'fail',
            severity: vr[col.key] === 0 ? 'critical' : vr[col.key] === 1 ? 'major' : 'minor',
            notes: normalizedNotes[vid] || null,
          }));

        vehicleEntries.push({
          vehicle_id: vid,
          odometer_km: odometerKm,
          score,
          issues_found: criticalCount,
          readings: vehicleReadings,
          items,
        });
      }

      // ONE call through the gateway — middleware handles the rest internally
      const res = await apiRequest('/inspection/bulk-save', {
        method: 'POST',
        body: JSON.stringify({
          vehicles: vehicleEntries,
          inspection_date: inspectionDate,
          inspector_name: inspectorName || null,
        }),
      });

      const data = res?.data || res || {};
      const saved = data.saved || 0;
      const failed = data.failed || [];

      if (failed.length > 0) {
        onMessage?.({
          title: 'Inspection incomplete',
          message: `${saved}/${selectedVehicleIds.length} vehicule(s) enregistre(s). ${failed.length} echec(s).`,
          type: 'error',
        });
      } else {
        onMessage?.({
          title: 'Inspection enregistree',
          message: `${data.readings_total || 0} mesures pour ${saved} vehicule(s) — ${data.inspections_created || 0} PV cree(s)`,
          type: 'success',
        });
      }
      onSuccess?.();
    } catch (err) {
      onMessage?.({
        title: 'Erreur',
        message: err.message || 'Erreur lors de l\'enregistrement',
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // ---- LED dots for step 2 next button ----
  const ledDots = currentStep === 2
    ? ALL_GROUPS.map(g => groupStatus(g, selectedVehicleIds, readings))
    : null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl my-4 flex flex-col max-h-[92vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 rounded-t-xl">
          <div className="flex items-center gap-3 text-white">
            <ClipboardCheck className="w-6 h-6" />
            <h2 className="text-lg font-bold">Inspection groupee</h2>
          </div>
          <div className="flex items-center gap-6">
            {/* Step indicator */}
            <div className="flex items-center gap-2">
              {STEP_LABELS.map((label, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold
                    ${currentStep === i + 1
                      ? 'bg-white text-blue-700'
                      : currentStep > i + 1
                        ? 'bg-blue-400 text-white'
                        : 'bg-blue-500/40 text-blue-200'
                    }`}
                  >
                    {i + 1}
                  </div>
                  <span className={`text-sm hidden sm:inline ${currentStep === i + 1 ? 'text-white font-medium' : 'text-blue-200'}`}>
                    {label}
                  </span>
                  {i < STEP_LABELS.length - 1 && <div className="w-6 h-px bg-blue-400/50" />}
                </div>
              ))}
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white p-1">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Incomplete warning bar */}
        {currentStep === 2 && incompleteWarningShown && (
          <div className="px-6 py-2 bg-yellow-50 border-b border-yellow-200 text-yellow-800 text-sm flex items-center gap-2">
            <span>⚠</span>
            <span>Certains groupes n'ont pas ete remplis. Cliquez a nouveau pour continuer malgre tout.</span>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {currentStep === 1 && (
            <InspectionStepSelection
              vehicles={vehicles}
              selectedVehicleIds={selectedVehicleIds}
              onSelectionChange={setSelectedVehicleIds}
              inspectionDate={inspectionDate}
              onDateChange={setInspectionDate}
              inspectorName={inspectorName}
              onInspectorChange={setInspectorName}
            />
          )}
          {currentStep === 2 && (
            <InspectionStepGrid
              vehicles={selectedVehicles}
              readings={readings}
              onReadingChange={setReading}
              odometers={odometers}
              onOdometerChange={setOdometer}
              setReadings={setReadings}
            />
          )}
          {currentStep === 3 && (
            <InspectionStepReview
              vehicles={selectedVehicles}
              vehiclesMap={vehiclesMap}
              readings={readings}
              odometers={odometers}
              vehicleNotes={vehicleNotes}
              onNoteChange={setNote}
              inspectionDate={inspectionDate}
              inspectorName={inspectorName}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50 rounded-b-xl">
          <div>
            {currentStep > 1 && (
              <button
                onClick={handleBack}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg flex items-center gap-2"
              >
                <ChevronLeft className="w-4 h-4" />
                Precedent
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg"
            >
              Annuler
            </button>
            {currentStep < 3 ? (
              <button
                onClick={handleNext}
                className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                Suivant
                {/* LED dots on step 2 */}
                {ledDots && (
                  <span className="flex items-center gap-1 ml-1">
                    {ledDots.map((dot, i) => (
                      <span
                        key={i}
                        className={`w-2 h-2 rounded-full ${
                          dot.status === 'empty' ? 'bg-gray-300' :
                          dot.status === 'partial' ? (dot.worst !== null ? RANGE_COLORS_MAP[dot.worst] : 'bg-gray-300') + ' opacity-60' :
                          (dot.worst !== null ? RANGE_COLORS_MAP[dot.worst] : 'bg-green-500')
                        }`}
                      />
                    ))}
                  </span>
                )}
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 disabled:opacity-50"
              >
                {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Simple color map for LED dots (avoids importing full RANGE_COLORS in template literal)
const RANGE_COLORS_MAP = {
  0: 'bg-red-500',
  1: 'bg-orange-400',
  2: 'bg-yellow-400',
  3: 'bg-green-500',
};
